import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExamById, getGroups, getQuestionsByGroupId, getOtherQuestions, updateQuestionEid, removeQuestion, getQuestionsDetail } from '../../api';
import Swal from 'sweetalert2';
import { toast, Toaster } from 'react-hot-toast';
import { 
  DocumentTextIcon,
  PlusCircleIcon,
  XMarkIcon,
  ArrowLeftIcon,
  FunnelIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon,
  AcademicCapIcon,
  TagIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

const UpdateExam = () => {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [examDetails, setExamDetails] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [examQuestions, setExamQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExamData();
    fetchGroups();
  }, [eid]);

  const fetchExamData = async () => {
    try {
      console.log('📥 Fetching exam data for eid:', eid);
      
      const [exam, questions] = await Promise.all([
        getExamById(eid),
        getQuestionsDetail(eid)
      ]);
      
      console.log('📊 Fetched exam:', exam);
      console.log('📝 Fetched questions:', questions);
      
      setExamDetails(exam);
      setExamQuestions(Array.isArray(questions) ? questions : []);
      toast.success('Exam details loaded');
      
    } catch (error) {
      console.error('❌ Failed to fetch exam data:', error);
      toast.error('Failed to load exam details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast.error('Failed to load question groups.');
    }
  };

  const handleGroupChange = async (e) => {
    const gid = e.target.value;
    setSelectedGroupId(gid);

    if (gid) {
      try {
        const questions = await getQuestionsByGroupId(gid);
        const otherQuestions = await getOtherQuestions(eid);

        const filteredQuestions = otherQuestions.filter(q =>
          questions.some(gq => gq.qid === q.qid)
        );

        setAvailableQuestions(filteredQuestions);
        toast.success(`Loaded ${filteredQuestions.length} question${filteredQuestions.length !== 1 ? 's' : ''}`);
      } catch (error) {
        console.error('Failed to fetch questions:', error);
        toast.error('Failed to load questions.');
        setAvailableQuestions([]);
      }
    } else {
      setAvailableQuestions([]);
    }
  };

  const handleAddQuestion = async (qid) => {
    try {
      console.log('➕ Adding question:', qid, 'to exam:', eid);
      
      const result = await updateQuestionEid(qid, eid);
      console.log('✅ Add Question Result:', result);

      if (result.status === 'success' || result.success === true) {
        toast.success('Question added to exam successfully!');
        
        await fetchExamData();
        
        if (selectedGroupId) {
          handleGroupChange({ target: { value: selectedGroupId } });
        }
      } else {
        toast.error(result.message || 'Failed to add question.');
      }
    } catch (error) {
      console.error('❌ Failed to add question:', error);
      toast.error('Failed to add question. Please try again.');
    }
  };

  const handleRemoveQuestion = async (qid) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to remove this question from the exam?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl px-6 py-2.5',
        cancelButton: 'rounded-xl px-6 py-2.5'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await removeQuestion(qid);
        console.log('Remove Question Result:', response);

        if (response.status === 'success' || response.success === true) {
          toast.success('Question removed from exam successfully!');
          await fetchExamData();
          
          if (selectedGroupId) {
            handleGroupChange({ target: { value: selectedGroupId } });
          }
        } else {
          toast.error(response.message || 'Failed to remove question.');
        }
      } catch (error) {
        console.error('Failed to remove question:', error);
        toast.error('Failed to remove question. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading exam details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Manage Exam Questions</h1>
                <p className="text-sm text-gray-500">Add or remove questions from this exam</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/exam')}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Exams</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Exam Details & Available Questions */}
          <div className="space-y-6">
            {/* Exam Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">{examDetails?.name}</h2>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                    <span className="text-sm font-semibold text-gray-600">Class</span>
                  </div>
                  <span className="font-bold text-gray-800">{examDetails?.class}</span>
                </div>

                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-600">Duration</span>
                  </div>
                  <span className="font-bold text-gray-800">{examDetails?.duration} min</span>
                </div>

                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <TrophyIcon className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm font-semibold text-gray-600">Total Marks</span>
                  </div>
                  <span className="font-bold text-gray-800">{examDetails?.tmarks}</span>
                </div>

                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-semibold text-gray-600">Status</span>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                    examDetails?.estatus === '1' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {examDetails?.estatus === '1' ? '✅ Published' : '📝 Draft'}
                  </span>
                </div>
              </div>
            </div>

            {/* Available Questions Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200 mb-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FunnelIcon className="h-5 w-5 text-purple-600" />
                  <label className="text-sm font-semibold text-gray-800">Select Question Group</label>
                </div>
                <select
                  value={selectedGroupId}
                  onChange={handleGroupChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                >
                  <option value="">-- Select Group --</option>
                  {groups.map(group => (
                    <option key={group.gid} value={group.gid}>
                      {group.Title}
                    </option>
                  ))}
                </select>
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <ListBulletIcon className="h-5 w-5 mr-2 text-blue-600" />
                Available Questions ({availableQuestions.length})
              </h3>

              {availableQuestions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">
                    {selectedGroupId ? 'No questions available in this group' : 'Select a group to view questions'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {availableQuestions.map(question => (
                    <div key={question.qid} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-4 hover:shadow-md transition-all">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-xs font-semibold text-gray-600">ID: {question.qid}</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                          <TrophyIcon className="h-3 w-3 mr-1" />
                          {question.mark} marks
                        </span>
                      </div>
                      
                      <div 
                        className="mb-3 text-sm text-gray-800 line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: question.question }}
                      />
                      
                      <button
                        onClick={() => handleAddQuestion(question.qid)}
                        className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-sm"
                      >
                        <PlusCircleIcon className="h-5 w-5" />
                        <span>Add to Exam</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Exam Questions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600" />
              Exam Questions ({examQuestions.length})
            </h3>

            {examQuestions.length === 0 ? (
              <div className="text-center py-20 bg-gray-50 rounded-xl">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DocumentTextIcon className="h-10 w-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-semibold mb-2">No questions added yet</p>
                <p className="text-gray-500 text-sm">Select a question group and add questions from the left panel</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
                {examQuestions.map((question, index) => (
                  <div key={question.qid} className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-5 hover:shadow-md transition-all">
                    <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-200">
                      <div>
                        <p className="font-semibold text-sm text-gray-600 mb-1">Question {index + 1}</p>
                        <p className="text-xs text-gray-500">ID: {question.qid}</p>
                        {(question.group_title || question.group_name) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 mt-2">
                            <TagIcon className="h-3 w-3 mr-1" />
                            {question.group_title || question.group_name}
                          </span>
                        )}
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                        <TrophyIcon className="h-3 w-3 mr-1" />
                        {question.mark} marks
                      </span>
                    </div>
                    
                    <div 
                      className="mb-3 text-sm text-gray-800 font-medium"
                      dangerouslySetInnerHTML={{ __html: question.question }}
                    />
                    
                    <div className="space-y-2 mb-4 pl-2">
                      {question.op1 && (
                        <div className="flex items-start text-sm bg-white p-2 rounded-lg border border-gray-200">
                          <span className="font-bold text-gray-700 mr-2 min-w-[20px]">A.</span>
                          <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: question.op1 }} />
                        </div>
                      )}
                      {question.op2 && (
                        <div className="flex items-start text-sm bg-white p-2 rounded-lg border border-gray-200">
                          <span className="font-bold text-gray-700 mr-2 min-w-[20px]">B.</span>
                          <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: question.op2 }} />
                        </div>
                      )}
                      {question.op3 && (
                        <div className="flex items-start text-sm bg-white p-2 rounded-lg border border-gray-200">
                          <span className="font-bold text-gray-700 mr-2 min-w-[20px]">C.</span>
                          <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: question.op3 }} />
                        </div>
                      )}
                      {question.op4 && (
                        <div className="flex items-start text-sm bg-white p-2 rounded-lg border border-gray-200">
                          <span className="font-bold text-gray-700 mr-2 min-w-[20px]">D.</span>
                          <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: question.op4 }} />
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleRemoveQuestion(question.qid)}
                      className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 px-4 py-2.5 rounded-lg font-semibold hover:bg-red-100 transition-all border border-red-200"
                    >
                      <XMarkIcon className="h-5 w-5" />
                      <span>Remove from Exam</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateExam;
